import React, { useCallback, useContext, useEffect, useState } from 'react';
import { getAllProgramFavoritesForEventAsync } from '../../services/api/data';
import moment from 'moment';
import 'moment/min/locales.min';
import { getItemAsync } from '../../services/api/graphQlRepository';
import { List } from 'react-md';
import ThemeContext from '../../components/Theme/ThemeContext';
import { TimezoneContext } from '../Timezone/context';
import styled from 'styled-components';
import { getString } from '../../services/api/store.js';
import Loader from '../../components/General/Loader';
import ClassifierIcon from '../../components/Icons/ClassifierIcon';
import ObjectListItem from '../../components/ObjectListItem';
import NavigationLink from '../../components/Navigation/NavigationLink';
import Placeholder from '../SearchPage/components/placeholder';
import AppointmentService from '../Appointments/services/AppointmentService';
import Auth from '../../services/api/auth';
import eventBus from '../../utils/eventBus';
import { APPOINTMENT_PARTICIPANT_STATUS } from '../Appointments/constants';
import { Tab, TabsContainer } from '../../components/DateTabs';
import sortBy from 'lodash/sortBy';
import { useSessionRegistrationStore } from '../sessionRegistration/stores/SessionRegistrationStore';

const LinkStyled = styled(NavigationLink)`
    text-decoration: none !important;
`;

const ListStyled = styled(List)`
    width: 100%;
    max-width: ${props => window.innerWidth}px;
`;

const ListItem = ({ item }) => (
    <span>
        <ObjectListItem item={item} type={item.typeObj.target.toLowerCase()} />
    </span>
);

const TAB_DATE_PATTERN = 'ddd DD';

const MyProgrammePage = props => {
    const [favorites, setFavorites] = useState([]);
    const [days, setDays] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState(0);

    const { selectedTimezone, getUtcToSelectedTimezone, getUnixToSelectedTimezone } = useContext(
        TimezoneContext,
    );

    const { registeredSessions } = useSessionRegistrationStore();

    const fetchData = useCallback(async () => {
        let favoritesArray = [];
        const daysArray = [];

        const myFavorites = await getAllProgramFavoritesForEventAsync();
        const favoriteIds = myFavorites.map(f => f.id);
        const registeredSessionsIds = registeredSessions.map(r => r.reference);
        const favorites = [...new Set([...favoriteIds, ...registeredSessionsIds])];

        for (const id of favorites) {
            const timeslot = await getItemAsync('timeslots', id);
            if (timeslot && timeslot.start) {
                timeslot.typeObj = await getItemAsync('types', timeslot.type);
                const day = getUtcToSelectedTimezone(timeslot.start).startOf('day').toISOString();
                timeslot.dayUtc = day;
                timeslot.sortTime = getUtcToSelectedTimezone(timeslot.start).toISOString();

                favoritesArray.push(timeslot);

                if (!daysArray.includes(day)) {
                    daysArray.push(day);
                }
            }
        }

        let appointments = [];
        const user = Auth.getUser();

        if (user) {
            appointments = await AppointmentService.getAllAppointments(user.id);
            // Filter appointments: keep only appointments that are not declined
            appointments = appointments.filter(shouldDisplayAppointment);
        }

        appointments.forEach(appointment => {
            const day = getUnixToSelectedTimezone(appointment.start).startOf('day').toISOString();

            appointment.dayUtc = day;

            if (!daysArray.includes(day)) {
                daysArray.push(day);
            }

            appointment.type = 'appointment';
            appointment.typeObj = { target: 'appointment' };
            appointment.sortTime = getUnixToSelectedTimezone(appointment.start).toISOString();
            favoritesArray.push(appointment);
        });

        favoritesArray = sortBy(favoritesArray, ['sortTime', 'orderingName', 'name']);

        const sortedDaysArray = daysArray.sort(
            (a, b) => moment(a).format('YYYYMMDD') - moment(b).format('YYYYMMDD'),
        );

        setFavorites(favoritesArray);
        setDays(sortedDaysArray);
        setLoading(false);
    }, [activeTab, selectedTimezone, registeredSessions]);

    useEffect(() => {
        fetchData();

        eventBus.on('refreshMyProgramme', fetchData);
    }, [activeTab, selectedTimezone, registeredSessions]);

    const shouldDisplayAppointment = appointment =>
        appointment &&
        appointment.members &&
        appointment.members.length &&
        appointment.members[0].AppointmentGroup &&
        (appointment.members[0].AppointmentGroup.status ===
            APPOINTMENT_PARTICIPANT_STATUS.WAITING ||
            appointment.members[0].AppointmentGroup.status ===
                APPOINTMENT_PARTICIPANT_STATUS.ACCEPTED);

    const onTabClick = index => {
        setActiveTab(index);
    };

    let dateSettings = getString('datetime');
    let locale = dateSettings && dateSettings.locale ? dateSettings.locale : 'en';
    const renderListItems = () =>
        favorites.map((item, i) => {
            if (!item || !item.typeObj) {
                return;
            }

            const isAppointment = item.type === 'appointment';

            if (days[activeTab] === item.dayUtc) {
                let params = item.params;
                if (typeof params === 'string') {
                    params = JSON.parse(item.params);
                }
                let classifierIcon;
                if (params && params.icons && params.icons.length) {
                    params.icons.forEach(item => {
                        classifierIcon = <ClassifierIcon key={item} imageId={item} />;
                    });
                }

                return (
                    <LinkStyled
                        key={'link_' + item.id}
                        params={{
                            type: isAppointment ? 'appointment' : 'detail',
                            objectClass: isAppointment ? null : item.typeObj.target.toLowerCase(),
                            objectId: item.id,
                        }}
                    >
                        <ListItem item={item} />
                    </LinkStyled>
                );
            }
        });

    const content = () => {
        if (!favorites.length && !loading) {
            return (
                <Placeholder
                    type="emptyList"
                    introText={getString('personalNoFavoritesTitle') || 'Nothing saved yet...'}
                />
            );
        } else {
            return <ListStyled className="">{renderListItems()}</ListStyled>;
        }
    };

    const renderTabs = theme => {
        return days.map((elem, index) => {
            const label = getUtcToSelectedTimezone(elem, TAB_DATE_PATTERN, false, locale, true);
            return (
                <Tab
                    key={`tab_${index}`}
                    active={index === activeTab}
                    color={theme.contrast}
                    onClick={() => onTabClick(index)}
                >
                    <div>{label.split(' ')[0]}</div>
                    <div>{label.split(' ')[1]}</div>
                </Tab>
            );
        });
    };

    return (
        <ThemeContext.Consumer>
            {({ theme }) => (
                <React.Fragment>
                    <TabsContainer>{renderTabs(theme)}</TabsContainer>
                    {loading && <Loader />}
                    {!loading && content()}
                </React.Fragment>
            )}
        </ThemeContext.Consumer>
    );
};

export default MyProgrammePage;
